import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';

const RealTimeDashboard = ({ userRole, organizationId, teamId }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(organizationId);
  const [organizations, setOrganizations] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const isAppAdmin = userRole === 'Admin';

  // Fetch organizations list (App Admin only)
  useEffect(() => {
    if (isAppAdmin) {
      fetchOrganizations();
    }
  }, [isAppAdmin]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (autoRefresh && (selectedOrg || teamId)) {
      const interval = setInterval(() => {
        fetchMetrics();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedOrg]);

  // Initial load
  useEffect(() => {
    if (teamId || selectedOrg || (isAppAdmin && !selectedOrg)) {
      fetchMetrics();
    }
  }, [selectedOrg, isAppAdmin, teamId]);

  const fetchOrganizations = async () => {
    try {
      const t = authService.getToken();
      const response = await fetch('/api/monitoring/organizations', {
        headers: { 'Authorization': `Bearer ${t}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      
      const t = authService.getToken();
      const endpoint = teamId && !selectedOrg
        ? `/api/monitoring/dashboard/team/${teamId}`
        : (isAppAdmin && !selectedOrg
            ? '/api/monitoring/dashboard/platform'
            : `/api/monitoring/dashboard/${selectedOrg}`);

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${t}` }
      });

      if (!response.ok) {
        let message = 'Failed to fetch metrics';
        if (response.status === 403) {
          message = 'Access denied. You do not have permission to view this dashboard.';
        } else if (response.status === 404) {
          message = teamId ? 'Team dashboard not found.' : 'Organization dashboard not found.';
        } else {
          try {
            const errorData = await response.json();
            if (errorData?.message) message = errorData.message;
          } catch {
            // ignore JSON parse errors
          }
        }
        throw new Error(message);
      }

      const data = await response.json();
      setMetrics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy': return { color: '#059669', bg: '#d1fae5' };
      case 'degraded': return { color: '#d97706', bg: '#fed7aa' };
      case 'unhealthy': return { color: '#dc2626', bg: '#fee2e2' };
      default: return { color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return '✓';
      case 'degraded': return '⚠';
      case 'unhealthy': return '✗';
      default: return '?';
    }
  };

  const formatTimestamp = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !metrics) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          <p style={{ color: '#6b7280' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b' }}>
        <strong>Error:</strong> {error}
        <button 
          onClick={fetchMetrics}
          style={{ marginLeft: 12, padding: '4px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No metrics available</div>;
  }

  const healthColors = getHealthStatusColor(metrics.systemHealth?.overall || 'healthy');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header with controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Real-Time Dashboard</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#6b7280' }}>
            Last updated: {formatTimestamp(lastUpdated)}
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {/* Organization selector (App Admin only) */}
          {isAppAdmin && (
            <select
              value={selectedOrg || 'platform'}
              onChange={(e) => setSelectedOrg(e.target.value === 'platform' ? null : parseInt(e.target.value))}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              <option value="platform">Platform Overview</option>
              {organizations.map(org => (
                <option key={org.organizationId} value={org.organizationId}>
                  {org.name} ({org.activeExecutionCount} active)
                </option>
              ))}
            </select>
          )}

          {/* Auto-refresh toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Auto-refresh (30s)</span>
          </label>

          {/* Manual refresh button */}
          <button
            onClick={fetchMetrics}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: loading ? '#9ca3af' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <span style={{ display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : 'none' }}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {/* Active Executions */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Active Executions</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>{metrics.activeExecutions || 0}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Currently running</div>
        </div>

        {/* Queue Depth */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Queue Depth</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>{metrics.queueDepth || 0}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {metrics.queueDepthDetails?.enqueued || 0} enqueued, {metrics.queueDepthDetails?.scheduled || 0} scheduled
          </div>
        </div>

        {/* API Response Time */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Response Time</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>{metrics.averageResponseTimeMs || 0}ms</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            P95: {metrics.p95ResponseTimeMs || 0}ms
          </div>
        </div>

        {/* Error Rate */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>Error Rate</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>
            {metrics.errorsPerHour ? metrics.errorsPerHour.toFixed(1) : '0.0'}/hr
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {metrics.totalErrors24h || 0} in last 24h
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="dashboard-card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>System Health</h3>
          <div style={{
            padding: '6px 12px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 500,
            color: healthColors.color,
            background: healthColors.bg
          }}>
            {getHealthStatusIcon(metrics.systemHealth?.overall || 'healthy')} {metrics.systemHealth?.overall || 'healthy'}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {metrics.systemHealth?.checks?.map((check, idx) => {
            const checkColors = getHealthStatusColor(check.status);
            return (
              <div key={idx} style={{
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${checkColors.bg}`,
                background: checkColors.bg
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{check.name}</span>
                  <span style={{ color: checkColors.color, fontSize: 18 }}>{getHealthStatusIcon(check.status)}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{check.message}</p>
                {check.responseTimeMs > 0 && (
                  <p style={{ margin: '4px 0 0 0', fontSize: 11, color: '#9ca3af' }}>{check.responseTimeMs}ms</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Time Series Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 }}>
        {/* Execution Volume Chart */}
        {metrics.timeSeriesData?.buckets && metrics.timeSeriesData.buckets.length > 0 && (
          <div className="dashboard-card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Execution Volume (24h)</h3>
            <div style={{ height: 250 }}>
              <MiniLineChart
                points={metrics.timeSeriesData.buckets.map(b => ({
                  label: new Date(b.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                  value: b.executionCount || 0
                }))}
                height={250}
                color="#2563eb"
              />
            </div>
          </div>
        )}

        {/* Response Time Chart */}
        {metrics.timeSeriesData?.buckets && metrics.timeSeriesData.buckets.length > 0 && (
          <div className="dashboard-card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>API Response Time (24h)</h3>
            <div style={{ height: 250 }}>
              <MiniLineChart
                points={metrics.timeSeriesData.buckets.map(b => ({
                  label: new Date(b.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                  value: b.averageResponseTimeMs || 0
                }))}
                height={250}
                color="#059669"
              />
            </div>
          </div>
        )}

        {/* Error Rate Chart */}
        {metrics.timeSeriesData?.buckets && metrics.timeSeriesData.buckets.length > 0 && (
          <div className="dashboard-card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Error Rate (24h)</h3>
            <div style={{ height: 250 }}>
              <MiniBarChart
                items={metrics.timeSeriesData.buckets.map(b => ({
                  label: new Date(b.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                  value: b.errorCount || 0
                }))}
                height={250}
                color="#dc2626"
              />
            </div>
          </div>
        )}

        {/* Queue Depth Details */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Hangfire Queue Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>Enqueued</span>
              <span style={{ fontWeight: 600 }}>{metrics.queueDepthDetails?.enqueued || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>Scheduled</span>
              <span style={{ fontWeight: 600 }}>{metrics.queueDepthDetails?.scheduled || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>Processing</span>
              <span style={{ fontWeight: 600 }}>{metrics.queueDepthDetails?.processing || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>Active Servers</span>
              <span style={{ fontWeight: 600 }}>{metrics.queueDepthDetails?.activeServers || 0}</span>
            </div>
            {isAppAdmin && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: 14, color: '#6b7280' }}>Succeeded (24h)</span>
                  <span style={{ fontWeight: 600, color: '#059669' }}>{metrics.queueDepthDetails?.succeeded || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, color: '#6b7280' }}>Failed (24h)</span>
                  <span style={{ fontWeight: 600, color: '#dc2626' }}>{metrics.queueDepthDetails?.failed || 0}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {/* Errors by Type */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Errors by Type (Top 5)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {metrics.errorsByType && metrics.errorsByType.length > 0 ? (
              metrics.errorsByType.map((error, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: 14 }}>{error.errorType || 'Unknown'}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{error.errorCode || ''}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>{error.count}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>
                      {formatTimestamp(error.lastOccurred)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ fontSize: 14, color: '#6b7280' }}>No errors in the last 24 hours</p>
            )}
          </div>
        </div>

        {/* Errors by User */}
        <div className="dashboard-card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Errors by User (Top 5)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {metrics.errorsByUser && metrics.errorsByUser.length > 0 ? (
              metrics.errorsByUser.map((user, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: 14 }}>{user.userName || 'Unknown'}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>{user.userId?.substring(0, 8)}...</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>{user.errorCount}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>
                      {formatTimestamp(user.lastError)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ fontSize: 14, color: '#6b7280' }}>No user errors in the last 24 hours</p>
            )}
          </div>
        </div>
      </div>

      {/* Platform-specific: Top Error Organizations */}
      {isAppAdmin && !selectedOrg && metrics.topErrorOrganizations && metrics.topErrorOrganizations.length > 0 && (
        <div className="dashboard-card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Organizations by Error Count (Top 5)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {metrics.topErrorOrganizations.map((org, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 12,
                background: '#f9fafb',
                borderRadius: 8
              }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 500 }}>{org.organizationName || 'Unknown'}</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>ID: {org.organizationId}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{org.errorCount}</p>
                  <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>errors in 24h</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Simple chart components (inline implementations)
function MiniLineChart({ points, height = 120, color = '#4c51bf' }) {
  if (!points || points.length === 0) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 12 }}>No data</div>;
  }
  const width = Math.max(200, points.length * 36);
  const maxVal = Math.max(1, ...points.map(p => p.value));
  const minVal = 0;
  const xStep = width / Math.max(1, points.length - 1);
  const yScale = (v) => height - (v - minVal) / (maxVal - minVal || 1) * (height - 20) - 10;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * xStep},${yScale(p.value)}`).join(' ');
  
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height, overflow: 'visible' }}>
      <path d={path} stroke={color} strokeWidth="2" fill="none" />
      {points.map((p, i) => (
        <circle key={i} cx={i * xStep} cy={yScale(p.value)} r="3" fill={color} />
      ))}
    </svg>
  );
}

function MiniBarChart({ items, height = 120, color = '#4c51bf' }) {
  if (!items || items.length === 0) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 12 }}>No data</div>;
  }
  const width = Math.max(200, items.length * 36);
  const maxVal = Math.max(1, ...items.map(i => i.value));
  const barWidth = width / items.length * 0.6;
  const barSpacing = width / items.length;
  const yScale = (v) => height - (v / maxVal) * (height - 20) - 10;
  
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height, overflow: 'visible' }}>
      {items.map((item, i) => (
        <rect
          key={i}
          x={i * barSpacing + (barSpacing - barWidth) / 2}
          y={yScale(item.value)}
          width={barWidth}
          height={height - yScale(item.value) - 10}
          fill={color}
        />
      ))}
    </svg>
  );
}

export default RealTimeDashboard;

